// «Инициативы» sub-tab of the Действия screen. Isolated .gn-* markup; logic +
// labels reused from cycle/Initiatives (initiativeExpectedEffectLabel) and
// cycle/shared (riskLabel/statusLabel/voteLabel/factionStanceLabel). Mutations
// (saveInitiative/withdrawInitiative/castInitiativeVote/setAgendaReadiness) via
// the shared action() wrapper. Phase-gated: AGENDA = authoring, VOTING = voting.

import { useMemo, useState, type ReactNode } from "react";
import {
  api,
  type CampaignSetup,
  type GameCycle,
  type Initiative,
  type InitiativeVoteStance,
} from "../api";
import { initiativeExpectedEffectLabel } from "../cycle/Initiatives";
import { factionStanceLabel, riskLabel, statusLabel, voteLabel } from "../cycle/shared";
import { GnCycleImage } from "./GnCycleImage";

type RunAction = (label: string, run: () => Promise<unknown>) => Promise<boolean>;
const GEN = "/assets/game-ui/gen";

function resMed(text: string): string {
  const s = text.toLowerCase();
  if (/убеж|housing|жиль|дом|бункер/.test(s)) return "res_housing";
  if (/провиз|food|еда|прод|паёк|припас/.test(s)) return "res_food";
  if (/энерг|energy|топлив|электр|генерат/.test(s)) return "res_energy";
  if (/связ|signal|коммуник|информ|радио/.test(s)) return "res_signal";
  if (/медик|medic|лекар|санитар|аптеч|лазарет/.test(s)) return "res_medicine";
  return "res_signal";
}
function effectMed(kind: string, text: string): string {
  if (kind === "THREAT_REDUCE") return "threat_unrest";
  if (kind === "RESOURCE_GAIN" || kind === "RESOURCE_PRESERVE") return resMed(text);
  if (kind === "KNOWLEDGE_GAIN") return "nav_chronicle";
  if (kind === "INSTITUTION_RELIEF") return "hook_institution";
  if (kind === "FACTION_RELATION") return "hook_faction";
  return "role_office";
}

function Tag({ med, children, warn }: { med: string; children: ReactNode; warn?: boolean }) {
  return (
    <span className={`gn-tag2${warn ? " warn" : ""}`}>
      <span className="gn-t2m">
        <img src={`${GEN}/${med}.png`} alt="" />
      </span>
      {children}
    </span>
  );
}

function GnAssessment({ initiative }: { initiative: Initiative }) {
  const a = initiative.assessment;
  if (!a) return null;
  const resources = a.resources ?? [];
  const effects = a.expectedEffects ?? [];
  const institutions = a.institutions ?? [];
  const factions = a.factions ?? [];
  return (
    <div className="gn-assess">
      <div className="gn-as">Системная оценка · {riskLabel(a.riskLevel)}</div>
      <div className="gn-as">Расходы</div>
      {resources.length === 0 ? (
        <div className="gn-tags">
          <span className="gn-tag2">Без прямого списания</span>
        </div>
      ) : (
        <div className="gn-tags">
          {resources.map((r) => (
            <Tag key={r.code} med={resMed(r.displayName)}>
              {r.displayName} −{r.cost} <em>из {r.available}</em>
            </Tag>
          ))}
        </div>
      )}
      {effects.length > 0 && (
        <>
          <div className="gn-as">Ожидаемый эффект</div>
          <div className="gn-tags">
            {effects.map((e, i) => (
              <Tag key={i} med={effectMed(e.kind, e.displayName)}>
                {initiativeExpectedEffectLabel(e.kind)}: {e.displayName}
              </Tag>
            ))}
          </div>
        </>
      )}
      {(institutions.length > 0 || factions.length > 0) && (
        <>
          <div className="gn-as">Исполнители · Фракции</div>
          <div className="gn-tags">
            {institutions.map((inst) => (
              <Tag key={inst.code} med="hook_institution">
                {inst.displayName} <em>{inst.role === "PRIMARY" ? "основной" : "поддержка"}</em>
              </Tag>
            ))}
            {factions.map((f) => (
              <Tag key={f.code} med="hook_faction" warn={f.stance === "RESISTANT"}>
                {f.displayName} <em>{factionStanceLabel(f.stance)}</em>
              </Tag>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function GnTally({ initiative, memberCount, voting }: { initiative: Initiative; memberCount: number; voting: boolean }) {
  const pending = Math.max(0, memberCount - initiative.supportCount - initiative.opposeCount - initiative.abstainCount);
  return (
    <div className="gn-tally">
      <div className="gn-t y">
        <b>{initiative.supportCount}</b>
        <span>Да</span>
      </div>
      <div className="gn-t n">
        <b>{initiative.opposeCount}</b>
        <span>Нет</span>
      </div>
      <div className="gn-t">
        <b>{voting ? pending : memberCount}</b>
        <span>{voting ? "Ждут" : "Участн."}</span>
      </div>
      <div className="gn-t">
        <b>{initiative.abstainCount}</b>
        <span>Возд.</span>
      </div>
    </div>
  );
}

function GnVoteControls({
  campaignId,
  initiative,
  currentUserId,
  action,
}: {
  campaignId: string;
  initiative: Initiative;
  currentUserId?: string;
  action: RunAction;
}) {
  const isCoauthor =
    initiative.isMine || initiative.coauthors.some((c) => c.userId === currentUserId);
  if (isCoauthor) {
    return <div className="gn-note">Ваше авторство автоматически считается голосом «за».</div>;
  }
  const stances: InitiativeVoteStance[] = ["SUPPORT", "ABSTAIN", "OPPOSE"];
  const cls: Record<InitiativeVoteStance, string> = { SUPPORT: "yes", ABSTAIN: "abs", OPPOSE: "no" };
  async function vote(stance: InitiativeVoteStance) {
    await action("Сохраняем голос…", () => api.castInitiativeVote(campaignId, initiative.id, stance));
  }
  return (
    <div className="gn-votes">
      {stances.map((s) => (
        <button
          key={s}
          type="button"
          className={`${cls[s]}${initiative.myVote === s ? " on" : ""}`}
          onClick={() => void vote(s)}
        >
          {voteLabel(s)}
        </button>
      ))}
    </div>
  );
}

function GnInitiativeCard({
  campaignId,
  initiative,
  voting,
  memberCount,
  currentUserId,
  action,
}: {
  campaignId: string;
  initiative: Initiative;
  voting: boolean;
  memberCount: number;
  currentUserId?: string;
  action: RunAction;
}) {
  const image = initiative.images.find((a) => a.status === "READY");
  return (
    <div className="gn-card gn-init">
      <div className="gn-ihead">
        <span className="gn-cameo">
          <img src={`${GEN}/ic_initiative.png`} alt="" />
        </span>
        <div className="gn-ihd">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="gn-stchip">● {statusLabel(initiative.status)}</span>
            <span className="gn-irisk">{riskLabel(initiative.riskLevel)}</span>
          </div>
          <h4>{initiative.title}</h4>
        </div>
      </div>

      {initiative.intendedOutcome && (
        <div className="gn-outcome">
          <div className="gn-ol">Ожидаемый результат</div>
          <p>{initiative.intendedOutcome}</p>
        </div>
      )}

      {image && (
        <div className="gn-detail-img">
          <GnCycleImage campaignId={campaignId} asset={image} />
        </div>
      )}

      <GnAssessment initiative={initiative} />

      <GnTally initiative={initiative} memberCount={memberCount} voting={voting} />

      {voting && (
        <GnVoteControls
          campaignId={campaignId}
          initiative={initiative}
          currentUserId={currentUserId}
          action={action}
        />
      )}

      {initiative.fundingExplanation && <div className="gn-note">{initiative.fundingExplanation}</div>}
    </div>
  );
}

function GnInitiativeForm({
  campaignId,
  cycle,
  existing,
  action,
}: {
  campaignId: string;
  cycle: GameCycle;
  existing?: Initiative;
  action: RunAction;
}) {
  const [title, setTitle] = useState(existing?.title ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [actionIds, setActionIds] = useState<string[]>(existing?.linkedActionIds ?? []);
  const [agreementIds, setAgreementIds] = useState<string[]>(existing?.linkedAgreementIds ?? []);
  const myActions = useMemo(() => cycle.actions.filter((a) => a.isMine), [cycle.actions]);

  const valid = title.trim().length >= 3 && description.trim().length >= 10;
  function toggle(list: string[], set: (v: string[]) => void, id: string) {
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }
  async function save() {
    if (!valid) return;
    await action("Оцениваем и сохраняем инициативу…", () =>
      api.saveInitiative(campaignId, {
        title: title.trim(),
        description: description.trim(),
        linkedActionIds: actionIds,
        linkedAgreementIds: agreementIds,
      }),
    );
  }
  async function withdraw() {
    await action("Отзываем инициативу…", () => api.withdrawInitiative(campaignId));
  }

  return (
    <div className="gn-card">
      <span className="gn-eyebrow">{existing ? "Моя инициатива" : "Новая инициатива"}</span>
      <input
        className="gn-input"
        placeholder="Название инициативы"
        maxLength={120}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        className="gn-ta"
        placeholder="Что именно должен решить Совет"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      {myActions.length > 0 && (
        <>
          <span className="gn-as">Результаты ваших действий</span>
          <div className="gn-coactors">
            {myActions.map((a) => {
              const ready = a.narrativeStatus === "READY";
              return (
                <label className={`gn-coactor${ready ? "" : " off"}`} key={a.id}>
                  <input
                    type="checkbox"
                    disabled={!ready}
                    checked={actionIds.includes(a.id)}
                    onChange={() => toggle(actionIds, setActionIds, a.id)}
                  />
                  <b>{a.intentSummary || a.description.slice(0, 48)}</b>
                  {!ready && <span>формируется</span>}
                </label>
              );
            })}
          </div>
        </>
      )}
      {cycle.agreements.length > 0 && (
        <>
          <span className="gn-as">Договорённости</span>
          <div className="gn-coactors">
            {cycle.agreements.map((ag) => (
              <label className="gn-coactor" key={ag.id}>
                <input
                  type="checkbox"
                  checked={agreementIds.includes(ag.id)}
                  onChange={() => toggle(agreementIds, setAgreementIds, ag.id)}
                />
                <b>{ag.title}</b>
              </label>
            ))}
          </div>
        </>
      )}
      {myActions.length === 0 && cycle.agreements.length === 0 && (
        <p className="gn-omuted" style={{ marginTop: "4px" }}>
          Сюда можно прикрепить результаты ваших действий и договорённости этого цикла — они появятся здесь, как только вы их совершите.
        </p>
      )}
      <div className="gn-form-actions">
        <button type="button" className="gn-cta" disabled={!valid} onClick={() => void save()}>
          {existing ? "Пересчитать" : "Рассчитать и сохранить"}
        </button>
        {existing && (
          <button type="button" className="gn-darkbtn" onClick={() => void withdraw()}>
            Отозвать
          </button>
        )}
      </div>
    </div>
  );
}

export function GnInitiativesTab({
  campaignId,
  setup,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: RunAction;
}) {
  const cycle = setup.cycle.current;
  const initiatives = useMemo(
    () =>
      cycle
        ? cycle.initiatives.filter((i) => i.status !== "MERGED" && i.consolidatedIntoId == null)
        : [],
    [cycle],
  );
  if (!cycle) return null;

  const voting = cycle.phase === "VOTING";
  const isAgenda = cycle.phase === "AGENDA";
  const currentUserId = setup.members.find((m) => m.isCurrentUser)?.userId;
  const memberCount = setup.members.length;
  const existing = cycle.initiatives.find((i) => i.isMine && i.status !== "MERGED");

  async function setAgendaReady(ready: boolean) {
    await action(ready ? "Готовимся к голосованию…" : "Возвращаемся к подготовке…", () =>
      api.setAgendaReadiness(campaignId, ready),
    );
  }

  return (
    <div className="gn-body">
      <div className="gn-card">
        <span className="gn-eyebrow">{voting ? "Голосование" : "Решения Совета"}</span>
        <div className="gn-init-desc">
          Инициативы закрепляют результаты действий как решения Совета
          {!isAgenda && !voting ? " — авторство откроется на этапе Совета." : "."}
        </div>
      </div>

      {isAgenda && cycle.canEditInitiative && (
        <GnInitiativeForm campaignId={campaignId} cycle={cycle} existing={existing} action={action} />
      )}

      {initiatives.length === 0 ? (
        <div className="gn-card gn-empty">Инициатив пока нет.</div>
      ) : (
        initiatives.map((i) => (
          <GnInitiativeCard
            key={i.id}
            campaignId={campaignId}
            initiative={i}
            voting={voting}
            memberCount={memberCount}
            currentUserId={currentUserId}
            action={action}
          />
        ))
      )}

      {isAgenda && (
        <button
          type="button"
          className="gn-darkbtn"
          disabled={!cycle.myAgendaReady && !cycle.canMarkAgendaReady}
          onClick={() => void setAgendaReady(!cycle.myAgendaReady)}
        >
          {cycle.myAgendaReady ? "Продолжить подготовку" : "Готов к голосованию"}
        </button>
      )}
    </div>
  );
}
